import type { CSSProperties, ReactNode } from 'react'

type StatSize = 'lg' | 'sm'
type StatTone = 'default' | 'danger' | 'warm' | 'positive' | 'accent' | 'blocking'

const TONE_COLOR: Record<StatTone, string> = {
  default: 'var(--text-1)',
  danger: 'var(--danger)',
  warm: 'var(--warm)',
  positive: 'var(--positive)',
  accent: 'var(--accent)',
  blocking: 'var(--rose)',
}

interface StatTileProps {
  /** The big metric (already formatted, e.g. "$42", "−16%", "#1"). */
  value: ReactNode
  /** Mono uppercase caption under the number. */
  label: ReactNode
  /** Optional delta string shown beside/under the value (e.g. "↓ 12% 30d"). */
  delta?: ReactNode
  deltaTone?: StatTone
  size?: StatSize
  tone?: StatTone
  /** Render a bordered tile (sm grid use). `false` = bare number (KPI strip). */
  boxed?: boolean
  className?: string
  style?: CSSProperties
  /** Native tooltip for metrics whose definition is not self-evident. */
  title?: string
}

/**
 * StatTile — a single metric: big mono number + mono uppercase label.
 * `size="lg"` is the dashboard KPI (44px); `size="sm"` is the 4-up detail-panel
 * stat (24px). Aurora+ source: `.kpi-card` (s04) and `.det-stat` (s12).
 */
export function StatTile({
  value,
  label,
  delta,
  deltaTone = 'default',
  size = 'lg',
  tone = 'default',
  boxed = size === 'sm',
  className,
  style,
  title,
}: StatTileProps) {
  const valueSize = size === 'lg' ? 44 : 24
  const labelSize = size === 'lg' ? 9 : 8.5

  const box: CSSProperties = boxed
    ? size === 'lg'
      ? {
          background: 'var(--stat-bg)',
          border: '1px solid var(--stat-border)',
          borderRadius: 'var(--r)',
          padding: 20,
        }
      : {
          background: 'var(--surface)',
          border: '1px solid var(--stat-border)',
          borderRadius: 10,
          padding: '10px 11px',
        }
    : {}

  return (
    <div className={className} title={title} style={{ ...box, ...style, cursor: title ? 'help' : undefined }}>
      <div
        className="tnum"
        style={{
          fontFamily: 'var(--mono)',
          fontWeight: 600,
          fontSize: valueSize,
          lineHeight: 1,
          color: TONE_COLOR[tone],
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: labelSize,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'var(--text-3)',
          marginTop: size === 'lg' ? 8 : 4,
        }}
      >
        {label}
      </div>
      {delta != null && (
        <div
          className="tnum"
          style={{
            fontFamily: 'var(--mono)',
            fontSize: size === 'lg' ? 11 : 10,
            fontWeight: 600,
            marginTop: 6,
            color: TONE_COLOR[deltaTone],
          }}
        >
          {delta}
        </div>
      )}
    </div>
  )
}
